import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Scorecard } from '@/components/scorecard/Scorecard';
import { Button, ButtonLink } from '@/components/ui/Button';
import { CourseTexture } from '@/components/ui/CourseTexture';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { VerifiedPill } from '@/components/ui/Pill';
import { SectionBar } from '@/components/ui/SectionBar';
import { useToast } from '@/components/ui/Toast';
import { usersApi } from '@/lib/api/endpoints';
import { completedRounds, qk, useAllRounds, useCourse } from '@/lib/api/queries';
import { useAuth, useCurrentUser } from '@/lib/auth/AuthContext';
import { cx } from '@/lib/cx';
import { fmtDateLong } from '@/lib/format/date';
import { fmtDecimal, fmtInt, fmtToPar } from '@/lib/format/number';
import { loadUnit, toUnit, unitLabel } from '@/lib/geo/units';
import { t } from '@/lib/i18n';
import { teeColor } from '@/routes/record/teeColor';
import { routes } from '@/routes/paths';
import styles from './Courses.module.css';
import { ChevronLeftIcon } from '@/components/icons';

export function CourseDetail() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { reloadUser } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const course = useCourse(courseId);
  const allRounds = useAllRounds();
  const unit = loadUnit();

  const [tee, setTee] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const data = course.data;
  const activeTee = data?.teeBoxes.find((t) => t.name === tee) ?? data?.teeBoxes[0] ?? null;

  const myRounds = useMemo(() => {
    if (!courseId) return [];
    return completedRounds(allRounds.data?.rounds ?? []).filter((r) => r.courseId === courseId);
  }, [allRounds.data, courseId]);

  const scorecardHoles = useMemo(
    () =>
      (data?.holes ?? []).map((h) => ({
        number: h.number,
        par: h.par,
        strokeIndex: h.handicapIndex,
      })),
    [data],
  );

  /** Per-hole yardage for the selected tee — the row that replaces the score rows here. */
  const teeDistances = useMemo(() => {
    const map = new Map<number, string>();
    if (!data || !activeTee) return map;
    for (const h of data.holes) {
      const meters = h.distancesByTee[activeTee.name];
      if (meters != null) map.set(h.number, String(toUnit(meters, unit)));
    }
    return map;
  }, [data, activeTee, unit]);

  if (course.isLoading) {
    return (
      <div className={styles.page}>
        <div style={{ background: 'var(--green)', padding: '2rem var(--gutter) 2.5rem' }}>
          <Skeleton width="60%" height={30} style={{ opacity: 0.25 }} />
          <Skeleton width="40%" height={12} style={{ marginTop: 12, opacity: 0.25 }} />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState
        title={t('course.notFound.title')}
        actions={
          <ButtonLink to={routes.courses} variant="secondary" block>
            {t('course.notFound.action')}
          </ButtonLink>
        }
      >
        {t('course.notFound.body')}
      </EmptyState>
    );
  }

  const isHome = user.homeCourseId === data.id;

  const toggleHome = async () => {
    setSaving(true);
    try {
      await usersApi.updateMe({ homeCourseId: isHome ? null : data.id });
      await reloadUser();
      await queryClient.invalidateQueries({ queryKey: qk.me });
      toast.success(
        isHome ? t('course.homeRemoved') : t('course.homeSet', { name: data.name }),
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('course.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <CourseTexture location={data.location} veil="header" className={styles.hero}>
        <div className={styles.heroNav}>
          <button
            type="button"
            className={styles.back}
            onClick={() => navigate(-1)}
            aria-label={t('rounds.back')}
          >
            <ChevronLeftIcon size={22} />
          </button>
        </div>

        <div className={styles.heroBody}>
          {data.verified && <VerifiedPill />}
          <h1 className={styles.courseName}>{data.name}</h1>
          <p className={styles.courseMeta}>
            {[
              data.city,
              t('course.holes', { count: data.totalHoles }),
              t('course.par', { value: data.parTotal }),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>

          <div className={styles.heroActions}>
            <ButtonLink to={routes.hole(data.id, 1)} variant="onInk" size="lg">
              {t('course.exploreHoles')}
            </ButtonLink>
            <ButtonLink to={routes.record} variant="onInkOutline" size="lg">
              {t('rounds.record')}
            </ButtonLink>
          </div>
        </div>
      </CourseTexture>

      <SectionBar variant="mid">{t('course.tees')}</SectionBar>
      <div className={styles.teeTable}>
        <div className={styles.teeHead}>
          <span className={styles.teeName}>{t('course.tee')}</span>
          <span className={styles.teeDistance}>{t('scorecard.distance')}</span>
          <span className={styles.teeCr}>{t('course.cr')}</span>
          <span className={styles.teeSlope}>{t('round.slope')}</span>
        </div>
        {data.teeBoxes.map((t) => (
          <button
            key={t.name}
            type="button"
            className={cx(styles.teeRow, t.name === activeTee?.name && styles.teeRowActive)}
            onClick={() => setTee(t.name)}
            aria-pressed={t.name === activeTee?.name}
          >
            <span className={styles.teeName}>
              <span
                className={styles.teeDot}
                style={{ background: teeColor(t) }}
                aria-hidden="true"
              />
              {t.name}
            </span>
            <span className={styles.teeDistance}>{toUnit(t.totalDistance, unit)}</span>
            <span className={styles.teeCr}>{fmtDecimal(t.courseRating)}</span>
            <span className={styles.teeSlope}>{t.slopeRating}</span>
          </button>
        ))}
        <p className={styles.teeNote}>{t('course.teeNote', { unit: unitLabel(unit) })}</p>
      </div>

      <SectionBar variant="mid" aside={t('round.scorecard.swipe')}>
        {t('course.scorecard')}
      </SectionBar>
      <Scorecard
        holes={scorecardHoles}
        showStrokeIndex
        variant="course"
        distances={teeDistances}
        distanceLabel={activeTee?.name}
        linkTo={(n) =>
          routes.hole(data.id, n) + `?tee=${encodeURIComponent(activeTee?.name ?? '')}`
        }
      />

      <SectionBar aside={t('rounds.count', { count: myRounds.length })}>
        {t('course.myRounds')}
      </SectionBar>
      {myRounds.length === 0 ? (
        <p className={styles.footerNote}>{t('course.noRoundsHere')}</p>
      ) : (
        myRounds.slice(0, 5).map((r) => (
          <Link key={r.id} to={routes.round(r.id)} className={styles.roundRow}>
            <span>
              <span className={styles.roundDate}>{fmtDateLong(r.completedAt ?? r.startedAt)}</span>
              <span className={styles.roundMeta}>
                {t('course.roundMeta', { tee: r.teeBoxName, holes: r.holesPlayed })}
              </span>
            </span>
            <span>
              <span className={styles.roundScore}>{fmtInt(r.totalStrokes)}</span>
              <span className={styles.roundToPar}>{fmtToPar(r.totalToPar)}</span>
            </span>
          </Link>
        ))
      )}

      <div className={styles.actions}>
        <Button
          variant={isHome ? 'secondary' : 'outline'}
          block
          size="lg"
          busy={saving}
          onClick={() => void toggleHome()}
        >
          {isHome ? t('course.isHome') : t('course.setHome')}
        </Button>
      </div>
    </div>
  );
}
